/**
 * Subscriptions module API client (T35.7.2 Part A).
 *
 * Thin wrappers for `/api/subscriptions`, routed through the app shell's shared
 * HttpClient so an expired access token refreshes and retries once.
 *
 * CP1/CP4: the accessor is injected from the app shell — no platform-shaped
 * code (no Capacitor import) crosses into this module.
 *
 * No `FeedbackIdentity` / `feedbackService` / `/api/feedback` dependency.
 */

import { ApiError, type HttpClient } from '@shopista/shared/api';

let client: HttpClient | null = null;

export function setSubscriptionsHttpClient(httpClient: HttpClient): void {
  client = httpClient;
}

function http(): HttpClient {
  if (!client) throw new Error('Subscriptions HTTP client has not been configured');
  return client;
}

function legacyError(error: unknown, fallback: string): never {
  if (error instanceof ApiError) {
    const err = new Error(fallback) as Error & { status?: number };
    err.status = error.status;
    throw err;
  }
  throw error;
}

/** Shared POST helper for the subscriptions endpoints. */
async function postSubscriptionsJson<T>(path: string, body: unknown): Promise<T> {
  try { return await http().postRaw(path, body) as T; }
  catch (error) { return legacyError(error, `POST ${path} failed: ${(error as ApiError).status}`); }
}

/** Shared GET helper for the subscriptions endpoints. */
async function getSubscriptionsJson<T>(path: string): Promise<T> {
  try { return await http().getRaw(path) as T; }
  catch (error) { return legacyError(error, `GET ${path} failed: ${(error as ApiError).status}`); }
}

/** A recurring schedule a subscription can carry (matches the server's CHECK vocab). */
export type RecurringSchedule = 'none' | 'weekly' | 'biweekly' | 'monthly';

export type SubscriptionType = 'donation' | 'backIdea';

/** The processor a subscription routes to (the adapter that owns the row). */
export type ProcessorKey = 'stub' | 'stripe' | 'paypal';

/** The fixed set of cancel reasons (mirrors the server's CHECK + the questionnaire). */
export type CancellationReason =
  | 'too_expensive'
  | 'not_using'
  | 'temporary'
  | 'idea_resolved'
  | 'technical_issue'
  | 'other';

/** What a subscription call returns. `status` is 'recorded' from the gated stub. */
export interface SubscriptionResult {
  id: string;
  type: SubscriptionType;
  /** The idea this subscription is tied to, or null for a general donation. */
  featureidea_id: string | null;
  /** The backed idea's title (backIdea only), for the self-manage list. */
  featureidea_title?: string | null;
  amount_cents: number;
  currency: string;
  recurring: RecurringSchedule;
  status: string;
  /** Which adapter owns the row (stub / stripe / paypal). */
  processor: ProcessorKey;
}

/** A processor entry in the session descriptor's `alternatives`. */
export interface SelectedProcessor {
  processor: ProcessorKey;
  label: string;
}

/** The `/session` descriptor — the primary session + the (lazy) alternatives. */
export interface SessionDescriptor {
  subscription_id: string;
  primary: {
    processor: ProcessorKey;
    sessionToken: string | null;
    status: string;
  };
  alternatives: SelectedProcessor[];
}

/** Back a published idea (backIdea type). `amountCents` is the whole-dollar amount × 100. */
export function backIdea(
  ideaId: string,
  amountCents: number,
  recurring: RecurringSchedule,
): Promise<SubscriptionResult> {
  return postSubscriptionsJson<SubscriptionResult>(`/api/subscriptions/${ideaId}/subscribe`, {
    amountCents,
    recurring,
  });
}

/** A general "support development" donation (not tied to any idea). */
export function donate(
  amountCents: number,
  recurring: RecurringSchedule,
): Promise<SubscriptionResult> {
  return postSubscriptionsJson<SubscriptionResult>('/api/subscriptions/', { amountCents, recurring });
}

/** Change a subscription's amount / recurring in place. */
export function changeSubscription(
  subscriptionId: string,
  amountCents: number,
  recurring: RecurringSchedule,
): Promise<SubscriptionResult> {
  return postSubscriptionsJson<SubscriptionResult>(`/api/subscriptions/${subscriptionId}/change`, {
    amountCents,
    recurring,
  });
}

/**
 * Cancel a subscription (reversible — Resume re-opens it). The optional reason +
 * note write one append-only cancellations row; passing neither still cancels
 * (easy-cancel — the questionnaire never blocks the cancel).
 */
export function cancelSubscription(
  subscriptionId: string,
  reason?: CancellationReason | null,
  note?: string | null,
): Promise<SubscriptionResult> {
  const body: Record<string, unknown> = {};
  if (reason) body.reason = reason;
  if (note) body.note = note;
  return postSubscriptionsJson<SubscriptionResult>(
    `/api/subscriptions/${subscriptionId}/cancel`,
    body,
  );
}

/** Resume a canceled recurring subscription on the same row (un-cancel). */
export function resumeSubscription(subscriptionId: string): Promise<SubscriptionResult> {
  return postSubscriptionsJson<SubscriptionResult>(`/api/subscriptions/${subscriptionId}/resume`, {});
}

/**
 * BUG34 — abandon an UNCHARGED subscription intent on the payment failure/cancel
 * path, so the never-charged row leaves the Settings → Subscriptions surface
 * immediately rather than lingering as a phantom subscription. Best-effort: the
 * server no-ops an unknown / already-charged row, and the dialog swallows any
 * error (it is closing on a failure either way — cleanup must never surface its
 * own error to the user).
 */
export async function abandonSubscription(subscriptionId: string): Promise<void> {
  try {
    await postSubscriptionsJson<{ abandoned: boolean }>(
      `/api/subscriptions/${subscriptionId}/abandon`,
      {},
    );
  } catch {
    /* best-effort cleanup — never block or surface on the failure path */
  }
}

/** List the caller's own subscriptions (feeds the self-manage surface). */
export function listSubscriptions(): Promise<SubscriptionResult[]> {
  return getSubscriptionsJson<SubscriptionResult[]>('/api/subscriptions');
}

/**
 * Read one subscription row by id (used by the post-payment confirmation poll).
 * No dedicated GET-by-id endpoint exists — reuse the list the self-manage surface
 * reads and find the row. Returns null when the row isn't in the list.
 */
export async function getSubscription(
  subscriptionId: string,
): Promise<SubscriptionResult | null> {
  const rows = await listSubscriptions();
  return rows.find((r) => r.id === subscriptionId) ?? null;
}

/**
 * (Re)create a payment session for a subscription — the processor-agnostic
 * handoff descriptor. `processor` selects an alternative (lazy, one session per
 * chosen processor); omit it for the server-chosen primary. The server
 * revalidates a requested key against the eligible set.
 */
export function createSession(
  subscriptionId: string,
  processor?: ProcessorKey,
): Promise<SessionDescriptor> {
  const body: Record<string, unknown> = {};
  if (processor) body.processor = processor;
  return postSubscriptionsJson<SessionDescriptor>(
    `/api/subscriptions/${subscriptionId}/session`,
    body,
  );
}
